import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { migrateAllNews, clearExistingArticles, migrateNewsArticles } from '../../utils/migrateNews';

const NewsMigration = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const isAdmin = hasRole('admin');

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Access denied. Admin privileges required.</p>
        </div>
      </div>
    );
  }

  const handleMigration = async (type) => {
    setLoading(true);
    setError('');
    setResults(null);

    try {
      let migrationResults;
      
      switch (type) {
        case 'full':
          migrationResults = await migrateAllNews(true); // Clear first
          break;
        case 'articles':
          migrationResults = await migrateNewsArticles();
          break;
        case 'clear':
          const cleared = await clearExistingArticles();
          migrationResults = { cleared, migrated: 0, errors: 0 };
          break;
        default:
          throw new Error('Invalid migration type');
      }
      
      setResults(migrationResults);
    } catch (err) {
      console.error('Migration error:', err);
      setError(err.message || 'Migration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">News Migration Tool</h1>
        
        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            This tool migrates news articles from static data to Firebase. Choose the migration type below:
          </p>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
            <div className="flex">
              <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Warning</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Full migration will clear all existing articles in Firebase. Make sure you have a backup if needed.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <button
            onClick={() => handleMigration('full')}
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Migrating...' : 'Full Migration (Clear + All Articles)'}
          </button>
          
          <button
            onClick={() => handleMigration('articles')}
            disabled={loading}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Migrating...' : 'Migrate Articles Only'}
          </button>
          
          <button
            onClick={() => handleMigration('clear')}
            disabled={loading}
            className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Clearing...' : 'Clear All Articles'}
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">❌ Error: {error}</p>
          </div>
        )}

        {results && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <h3 className="text-lg font-semibold text-green-800 mb-2">Migration Results</h3>
            <div className="text-green-700">
              {results.cleared !== undefined && (
                <p>🗑️ Cleared: {results.cleared} articles</p>
              )}
              {results.migrated !== undefined && (
                <p>✅ Migrated: {results.migrated} articles</p>
              )}
              {results.errors !== undefined && results.errors > 0 && (
                <p>❌ Errors: {results.errors}</p>
              )}
            </div>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Sample Articles Included:</h3>
          <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
            <li>APN Q1 Newsletter</li>
            <li>Network Growth Update</li>
            <li>Industry Insights: M&A Trends 2022</li>
            <li>APN Chicago Event Recap</li>
          </ul>
          
          <h3 className="text-sm font-medium text-blue-800 mb-2 mt-4">Next Steps After Migration:</h3>
          <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
            <li>Check the News page to verify data loaded correctly</li>
            <li>Test news article detail pages</li>
            <li>Update the News page to use Firebase data instead of static files</li>
            <li>Test admin article creation and management features</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default NewsMigration;
